import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaHome, 
  FaProjectDiagram, 
  FaCalendarCheck,
  FaUsers, 
  FaFileInvoiceDollar,
  FaShieldAlt,
  FaConciergeBell,
  FaReceipt,
  FaSignOutAlt,
  FaUserCog,
  FaCrown,
  FaStickyNote,
  FaClipboardCheck
} from 'react-icons/fa';
import { logoutUser } from '../../redux/slices/authSlice';
import { setSidebarOpen } from '../../redux/slices/uiSlice';
import useTrialStatus from '../../hooks/useTrialStatus';
import { useOverlayBack } from '../../hooks/useSmartBack';
import PlanUpgradeModal from '../PlanUpgradeModal/PlanUpgradeModal';
import { useLanguage } from '../../context/LanguageContext';
import './Sidebar.scss';

const Sidebar = () => {
  const { sidebarOpen } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);
  const trialStatus = useTrialStatus();
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('');
  const [isMobileDrawer, setIsMobileDrawer] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  // Track mobile breakpoint (drawer is an overlay only on small screens —
  // on desktop the sidebar is persistent and must NOT capture back).
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobileDrawer(e.matches);
    setIsMobileDrawer(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Step-by-step back: with the mobile drawer open, back closes the drawer
  // first instead of leaving the page.
  const closeDrawer = React.useCallback(() => dispatch(setSidebarOpen(false)), [dispatch]);
  useOverlayBack(sidebarOpen && isMobileDrawer, closeDrawer, 'sidebar');
  
  const isAdmin = user?.role === 'admin';
  const { t } = useLanguage();
  
  // Check if user has Gold plan - default to false if trialStatus not ready
  const isGoldPlan = trialStatus?.isGoldPlan === true;

  // Define menu items with gold-only flag
  const menuItems = [
    { path: '/dashboard', icon: <FaHome />, label: t('sidebar.dashboard'), goldOnly: false },
    { path: '/quotations', icon: <FaFileInvoiceDollar />, label: t('sidebar.quotations'), goldOnly: true },
    { path: '/events', icon: <FaCalendarCheck />, label: t('sidebar.events'), goldOnly: false },
    { path: '/billing', icon: <FaReceipt />, label: t('sidebar.billing'), goldOnly: true },
    { path: '/checklists', icon: <FaClipboardCheck />, label: t('sidebar.checklist'), goldOnly: false },
    { path: '/notes', icon: <FaStickyNote />, label: t('sidebar.notes'), goldOnly: false },
    { path: '/team', icon: <FaUsers />, label: t('sidebar.team'), goldOnly: false },
    { path: '/projects', icon: <FaProjectDiagram />, label: t('sidebar.projects'), goldOnly: false },
    { path: '/policy', icon: <FaShieldAlt />, label: t('sidebar.policy'), goldOnly: true },
    { path: '/services', icon: <FaConciergeBell />, label: t('sidebar.services'), goldOnly: false },
  ];
  
  // Add admin panel link for admin users
  if (isAdmin) {
    menuItems.push({ path: '/admin', icon: <FaUserCog />, label: t('sidebar.adminPanel'), goldOnly: false });
  }

  // For free trial and silver, show accessible items first, gold-only items last
  const sortedMenuItems = isGoldPlan ? menuItems : [...menuItems].sort((a, b) => {
    if (a.goldOnly === b.goldOnly) return 0;
    return a.goldOnly ? 1 : -1;
  });

  // Handle clicking on restricted items
  const handleRestrictedClick = (e, item) => {
    if (item.goldOnly && !isGoldPlan) {
      e.preventDefault();
      setUpgradeFeatureName(item.label);
      setShowUpgradeModal(true);
    } else {
      dispatch(setSidebarOpen(false));
    }
  };

  useEffect(() => {
    function handleOutsideClick(e) {
      if (!sidebarRef.current) return;
      if (sidebarRef.current.contains(e.target)) return;
      if (e.target.closest('.navbar__menu-btn')) return;
      if (sidebarOpen) {
        dispatch(setSidebarOpen(false));
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [dispatch, sidebarOpen]);

  return (
    <>
      <aside ref={sidebarRef} className={`sidebar ${sidebarOpen ? 'sidebar--open' : 'sidebar--closed'}`}>
        <nav className="sidebar__nav">
          {sortedMenuItems.map((item) => {
            const isRestricted = item.goldOnly && !isGoldPlan;
            
            return (
              <NavLink
                key={item.path}
                to={isRestricted ? '#' : item.path}
                onClick={(e) => handleRestrictedClick(e, item)}
                className={({ isActive }) => 
                  `sidebar__item ${isActive && !isRestricted ? 'sidebar__item--active' : ''} ${isRestricted ? 'sidebar__item--restricted' : ''}`
                }
                title={isRestricted ? `${item.label} requires Gold plan` : item.label}
              >
                <span className="sidebar__icon">{item.icon}</span>
                {sidebarOpen && (
                  <>
                    <span className="sidebar__label">{item.label}</span>
                    {isRestricted && <FaCrown className="sidebar__gold-badge" title="Gold Plan Required" />}

                  </>
                )}

              </NavLink>
            );
          })}
        </nav>

        {/* Footer with logout */}
        <div className="sidebar__footer">
          <LogoutButton sidebarOpen={sidebarOpen} />
        </div>
      </aside>

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
        currentPlan={trialStatus?.planTier || 'free'}
      />
    </>
  );
};

const LogoutButton = ({ sidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  const handleLogout = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await dispatch(logoutUser());
      dispatch(setSidebarOpen(false));
      navigate('/login');
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <button
      className="sidebar__item sidebar__logout"
      onClick={handleLogout}
      type="button"
      disabled={isSigningOut}
    >
      {sidebarOpen ? (
        <>
          <span className="sidebar__icon"> <FaSignOutAlt /> </span>
          <span className="sidebar__label">{t('sidebar.logout')}</span>
        </>
      ) : (
        <span className="sidebar__icon"> <FaSignOutAlt /> </span>
      )}
    </button>
  );
};

export default Sidebar;
